import * as yup from 'yup'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  FormGroup,
  FormHelperText,
  Grid,
  InputLabel,
  TextField,
  Typography,
} from '@mui/material'
import Field from '../Field'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import SpacerBox from '../SpacerBox'
import { useTranslation } from 'react-i18next'
import InlineValidationError from './InlineValidationError'
import InputArrivalDate, { arrivalDateSchema } from './InputArrivalDate'

export interface DeleteAnimalFormValues {
  arrivalDate: Date
  microchipNumber: string
}

export function DeleteAnimalForm({
  microchipNumber,
  onSubmit,
  onCancel,
  isSubmitting,
}: {
  microchipNumber: string
  onSubmit: (values: DeleteAnimalFormValues) => void
  onCancel: () => void
  isSubmitting: boolean
}) {
  const { t } = useTranslation('common')

const schema = yup.object().shape({
    arrivalDate: arrivalDateSchema(),
    microchipNumber: yup
      .string()
      .min(15,t('registrationForm.error.microchipNumber.notEnough') as string)
      .max(15)
      .oneOf([microchipNumber], t('dogView.dialogDeleteMicrochipCode.matchError', { microchipNumber }) as string)
      .required(t('registrationForm.error.ifEmpty.generic') as string   ),
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<DeleteAnimalFormValues>({ resolver: yupResolver(schema) })

  return (
    <SpacerBox gap={3} data-qa="delete-animal-form">
      <SpacerBox gap={2} data-qa="current-chipnumber">
        <Typography variant="body1">{t('dogView.dialogDeleteAnimal.helpText')}</Typography>
        <Divider />
            <Field
                title={t('dogView.dialogChangeMicrochipCode.currentMicrochipNumber')}
                text={microchipNumber}
            />
      </SpacerBox>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <FormGroup sx={{ display: 'grid', gap: 4, mt: 2, width: '100%' }}>
          <FormControl>
            <InputArrivalDate control={control} errors={errors} />
          </FormControl>
          <FormControl>
            <InputLabel htmlFor="microchip-code">
              {t('common.animal.microchipCode')} <i aria-hidden={true}>*</i>
            </InputLabel>
            <TextField
              {...register('microchipNumber')}
              id="microchip-code"
              data-qa="microchip-number"
              error={!!errors.microchipNumber}
              inputProps={{
                type: 'text',
                inputMode: 'numeric',
                maxLength: 15,
              }}
            />
            {!!errors.microchipNumber ? (
              <InlineValidationError content={errors.microchipNumber?.message} />
            ) : (
              <FormHelperText>{t('dogView.dialogDeleteAnimal.confirmMicrochip')}</FormHelperText>
            )}
          </FormControl>
          <Grid container justifyContent="flex-end">
            <Button variant="text" disabled={isSubmitting} onClick={onCancel}>
              {t('dogView.dialogDeleteAnimal.cancel')}
            </Button>
            <Button variant="text" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <Box aria-label={t('common.loading')}>
                  <CircularProgress size={10} /> {t('common.loading') + '...'}
                </Box>
              ) : (
                t('dogView.dialogDeleteAnimal.confirm')
              )}
            </Button>
          </Grid>
        </FormGroup>
      </Box>
    </SpacerBox>
  )
}